import { useState, useEffect, useMemo } from 'react'
import { clsx } from 'clsx'

import {
  Button,
  Flex,
  PlusIcon,
  ScrollArea,
  ToggleGroup,
  VisuallyHidden,
} from '@buffer-mono/popcorn'

import ChannelGroupsItem from './ChannelGroupItem'
import { useVisibleElements } from '~publish/hooks/useVisibleElements'
import { useChannelGroupsContext } from '../../hooks/channelGroupsContext'

import styles from './ChannelGroups.module.css'
import AppActionCreators from '../../action-creators/AppActionCreators'
import { useSelectedProfiles } from '~publish/hooks/useSelectedProfiles'

const ChannelGroups = (): JSX.Element | null => {
  const [isExpanded, setIsExpanded] = useState(false)
  const { channelGroups, selectedChannelGroupIds } = useChannelGroupsContext()
  const { selectedProfiles } = useSelectedProfiles()

  // Filter out channel groups which are empty/have no channels
  // This can happen when a user has disconnected all channels from a channel group
  const filteredChannelGroups = useMemo(() => {
    return channelGroups?.filter((channelGroup) => {
      return (channelGroup.channels?.length || 0) > 0
    })
  }, [channelGroups])

  const { containerRef, measurementRef, visibleCount, hiddenCount } =
    useVisibleElements({ minimumVisibleCount: 1 })

  const visibleChannelGroupCount = visibleCount - 1 // - 1 to account for select all button

  const visibleChannelGroups = useMemo(() => {
    return isExpanded
      ? filteredChannelGroups
      : filteredChannelGroups?.slice(0, visibleChannelGroupCount)
  }, [isExpanded, filteredChannelGroups, visibleChannelGroupCount])

  // Get hidden channel groups (those beyond visibleChannelGroupCount)
  const hiddenChannelGroups = useMemo(() => {
    return hiddenCount > 0
      ? filteredChannelGroups?.slice(visibleChannelGroupCount)
      : []
  }, [filteredChannelGroups, hiddenCount, visibleChannelGroupCount])

  const hasHiddenGroupsSelected = useMemo(() => {
    return (
      hiddenChannelGroups &&
      hiddenChannelGroups.some((channelGroup) => {
        return selectedChannelGroupIds.has(String(channelGroup.id))
      })
    )
  }, [hiddenChannelGroups, selectedChannelGroupIds])

  const hasNoProfilesSelected = selectedProfiles.length === 0
  const hasChannelGroups = channelGroups && channelGroups.length > 0

  const handleExpand = (): void => {
    setIsExpanded(!isExpanded)
  }

  const onToggleSelectAll = (): void => AppActionCreators.toggleAllProfiles()

  // Handle clicks outside the component
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (
        containerRef.current &&
        event.target instanceof Node &&
        !containerRef.current.contains(event.target) &&
        isExpanded
      ) {
        setIsExpanded(false)
      }
    }

    // Add event listener when component is expanded
    if (isExpanded) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    // Cleanup event listener
    return (): void => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isExpanded, containerRef])

  return (
    <ToggleGroup
      type="multiple"
      className={clsx(styles.channelGroups, isExpanded && styles.expanded)}
      ref={containerRef}
    >
      <Flex ref={measurementRef} className={styles.channelGroupsMeasurement}>
        <Flex className={clsx(styles.toggleSelectAll, styles.hasGroups)}>
          <Button onClick={onToggleSelectAll} variant="tertiary" size="small">
            Unselect All
          </Button>
        </Flex>
        {filteredChannelGroups?.map((channelGroup) => (
          <ChannelGroupsItem
            key={`measurement-${channelGroup.id}`}
            channelGroup={channelGroup}
            isSelected={selectedChannelGroupIds.has(String(channelGroup.id))}
          />
        ))}
        <Button
          variant="secondary"
          size="medium"
          className={clsx(styles.remainingCount)}
        >
          <PlusIcon />
          10
        </Button>
      </Flex>

      <ScrollArea
        key={
          isExpanded ? 'channel-groups-expanded' : 'channel-groups-collapsed'
        }
        gradientFade={!isExpanded ? undefined : 15}
      >
        <Flex wrap="wrap" gap="xs">
          <VisuallyHidden id="channel-groups-description">
            Select one of your channel groups to quick select the channels in it
            to post to.{' '}
          </VisuallyHidden>
          <Flex
            className={clsx(
              styles.toggleSelectAll,
              hasChannelGroups && styles.hasGroups,
            )}
          >
            <Button onClick={onToggleSelectAll} variant="tertiary" size="small">
              {hasNoProfilesSelected ? 'Select All' : 'Unselect All'}
            </Button>
          </Flex>
          {visibleChannelGroups?.map((channelGroup) => (
            <ChannelGroupsItem
              key={`channel-group-${channelGroup.id}`}
              channelGroup={channelGroup}
              isSelected={selectedChannelGroupIds.has(String(channelGroup.id))}
            />
          ))}
          {hiddenChannelGroups &&
            hiddenChannelGroups.length > 0 &&
            !isExpanded && (
              <Button
                variant="secondary"
                size="medium"
                className={clsx(
                  styles.remainingCount,
                  hasHiddenGroupsSelected && styles.selected,
                )}
                onClick={handleExpand}
              >
                <PlusIcon />
                {hiddenCount}
              </Button>
            )}
        </Flex>
      </ScrollArea>
    </ToggleGroup>
  )
}

export default ChannelGroups
